import React, { useState, useEffect, useRef } from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
} from "@mui/material";
import { MultiSelect } from "react-multi-select-component";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db, loginAdmin } from "../../Firebase";
import toast from "react-hot-toast";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import NotificationsTable from "./NotificationsTable";
import ScheduledNotificationsTable from "./ScheduledNotificationsTable";

const SCHEDULE_COLLECTION = "vortex_scheduled_notifications";

// Returns the current local datetime string for the `min` attribute of datetime-local
const getMinDateTime = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
};

const Notifications = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [notificationText, setNotificationText] = useState("");
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationsList, setNotificationsList] = useState([]);
  const [selectFromMS, setselectFromMS] = useState([]);

  // Scheduling state
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState("");
  const [scheduledNotificationsList, setScheduledNotificationsList] = useState([]);

  // Ref so the polling interval always reads fresh state without stale closure
  const latestScheduledRef = useRef([]);
  useEffect(() => {
    latestScheduledRef.current = scheduledNotificationsList;
  }, [scheduledNotificationsList]);

  // ── Fetch sent notifications from Firestore ──────────────────────────────────
  const fetchUserNotification = async () => {
    try {
      await loginAdmin();
      const notificationsSnapshot = await getDocs(collection(db, "notifications"));
      const list = notificationsSnapshot.docs.map((d) => {
        const data = d.data();
        const rawDate = data.sentAt?.toDate
          ? data.sentAt.toDate()
          : data.createdAt?.toDate
          ? data.createdAt.toDate()
          : new Date();
        return {
          id: d.id,
          _rawDate: rawDate,
          createdAt: rawDate.toLocaleString(),
          username: "Admin",
          notificationType: data.title || data.notificationType || "",
          notificationText: data.body || data.notificationText || "",
        };
      });
      list.sort((a, b) => b._rawDate - a._rawDate);
      setNotificationsList(list);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Failed to load notifications");
    }
  };

  // ── Fetch scheduled notifications from Firestore ─────────────────────────────
  const fetchScheduledNotifications = async () => {
    try {
      await loginAdmin();
      const snap = await getDocs(collection(db, SCHEDULE_COLLECTION));
      const list = snap.docs
        .map((d) => {
          const data = d.data();
          const scheduledAt = data.scheduledAt?.toDate
            ? data.scheduledAt.toDate()
            : new Date(data.scheduledAt);
          return {
            id: d.id,
            _scheduledAt: scheduledAt,
            scheduledFor: scheduledAt.toLocaleString(),
            notificationType: data.title || "",
            notificationText: data.body || "",
            userCount: data.userIds?.length ?? 0,
            userIds: data.userIds || [],
            status: data.status || "pending",
          };
        })
        .sort((a, b) => b._scheduledAt - a._scheduledAt);
      setScheduledNotificationsList(list);
    } catch (err) {
      console.error("Error fetching scheduled notifications:", err);
    }
  };

  // ── Send any overdue pending notifications to Vortex Firebase ────────────────
  const checkAndSendDue = async () => {
    const list = latestScheduledRef.current;
    const now = new Date();
    const pending = list.filter(
      (n) => n.status === "pending" && n._scheduledAt <= now
    );
    if (pending.length === 0) return;

    let didSend = false;
    for (const notif of pending) {
      try {
        await loginAdmin();
        const newNotification = {
          title: notif.notificationType,
          body: notif.notificationText,
          sentToUserIds: notif.userIds,
          type: "admin_broadcast",
          sentAt: new Date(),
        };
        const docRef = await addDoc(collection(db, "notifications"), newNotification);

        // Write to each user's sub-collection
        for (const userId of notif.userIds) {
          await addDoc(collection(db, "users", userId, "notifications"), {
            id: docRef.id,
            title: notif.notificationType,
            body: notif.notificationText,
            createdAt: new Date(),
            read: false,
            type: "admin_broadcast",
          });
        }

        // Mark as sent
        await updateDoc(doc(db, SCHEDULE_COLLECTION, notif.id), {
          status: "sent",
          sentAt: new Date(),
        });
        toast.success(`Scheduled notification "${notif.notificationType}" sent!`);
        didSend = true;
      } catch (err) {
        console.error("Error sending scheduled notification:", err);
        try {
          await loginAdmin();
          await updateDoc(doc(db, SCHEDULE_COLLECTION, notif.id), { status: "failed" });
        } catch (_) {}
        toast.error(`Failed to send scheduled notification: "${notif.notificationType}"`);
      }
    }
    if (didSend) {
      fetchUserNotification();
      fetchScheduledNotifications();
    }
  };

  // ── On mount: load data and start polling interval ───────────────────────────
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        await loginAdmin();
        const userSnapshot = await getDocs(collection(db, "users"));
        const usersList = userSnapshot.docs.map((d) => ({
          id: d.id,
          uid: d.data().uid || d.id,
          ...d.data(),
        }));
        setUsers(
          usersList
            ?.filter((u) => !!u.name?.trim())
            ?.map((u) => ({ value: u.id || u.uid, label: u.name, email: u.email || "" })) || []
        );
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUserNotification();
    fetchUsers();
    fetchScheduledNotifications();

    // Check every 60 seconds for due scheduled notifications
    const interval = setInterval(checkAndSendDue, 60_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleNotificationTitle = (e) => {
    const v = e.target.value;
    setNotificationTitle(v.charAt(0).toUpperCase() + v.slice(1));
  };

  const handleNotificationTextChange = (e) => {
    const v = e.target.value;
    setNotificationText(v.charAt(0).toUpperCase() + v.slice(1));
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    const userIdsTemp = selectFromMS.map((item) => item.value);

    if (!notificationTitle || !notificationText) {
      toast.error("Title or text is empty");
      setOpen(false);
      return;
    }

    // ── SCHEDULE MODE ──────────────────────────────────────────────────────────
    if (scheduleMode) {
      if (!scheduledDateTime) {
        toast.error("Please select a date and time to schedule");
        setOpen(false);
        return;
      }
      const scheduledAt = new Date(scheduledDateTime);
      if (scheduledAt <= new Date()) {
        toast.error("Scheduled time must be in the future");
        setOpen(false);
        return;
      }
      try {
        await loginAdmin();
        await addDoc(collection(db, SCHEDULE_COLLECTION), {
          title: notificationTitle,
          body: notificationText,
          userIds: userIdsTemp,
          scheduledAt,
          status: "pending",
          createdAt: new Date(),
          platform: "vortex",
        });
        toast.success("Notification scheduled successfully! 📅");
        setOpen(false);
        setNotificationTitle("");
        setNotificationText("");
        setselectFromMS([]);
        setScheduledDateTime("");
        fetchScheduledNotifications();
      } catch (err) {
        setOpen(false);
        toast.error("Failed to schedule notification");
        console.error(err);
      }
      return;
    }

    // ── IMMEDIATE SEND (Vortex → Firestore) ───────────────────────────────────
    try {
      await loginAdmin();
      const newNotification = {
        title: notificationTitle,
        body: notificationText,
        sentToUserIds: userIdsTemp,
        type: "admin_broadcast",
        sentAt: new Date(),
      };
      const docRef = await addDoc(collection(db, "notifications"), newNotification);

      for (const userId of userIdsTemp) {
        await addDoc(collection(db, "users", userId, "notifications"), {
          id: docRef.id,
          title: notificationTitle,
          body: notificationText,
          createdAt: new Date(),
          read: false,
          type: "admin_broadcast",
        });
      }

      toast.success("Notification sent successfully");
      setOpen(false);
      setNotificationTitle("");
      setNotificationText("");
      setselectFromMS([]);
      fetchUserNotification();
    } catch (err) {
      setOpen(false);
      toast.error("Error sending notification");
      console.error(err);
    }
  };

  const handleDeleteScheduled = async (id) => {
    try {
      await loginAdmin();
      await deleteDoc(doc(db, SCHEDULE_COLLECTION, id));
      toast.success("Scheduled notification cancelled");
      fetchScheduledNotifications();
    } catch (err) {
      toast.error("Failed to cancel notification");
      console.error(err);
    }
  };

  // ── Confirmation dialog label ─────────────────────────────────────────────
  const confirmMessage = scheduleMode
    ? `Schedule this notification for ${scheduledDateTime ? new Date(scheduledDateTime).toLocaleString() : "the selected time"}?`
    : "Do you really want to send this notification now?";

  return (
    <div style={{
      flex: 1,
      minHeight: "100vh",
      backgroundColor: "#374151",
      color: "white",
      padding: "24px 16px",
      boxSizing: "border-box",
      overflowX: "hidden",
    }}>
      <ToastContainer />

      {/* ── Confirmation Dialog ───────────────────────────────────────────── */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>{confirmMessage}</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="secondary">No</Button>
          <Button onClick={handleNotificationSubmit} color="error">Yes</Button>
        </DialogActions>
      </Dialog>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <h1 style={{
        margin: "0 0 24px 0",
        fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
        fontWeight: 600,
        textTransform: "capitalize",
      }}>
        Notification
      </h1>

      {/* ── Form ─────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "20px", maxWidth: "900px" }}>

        {/* Title + Users — side by side on desktop, stacked on mobile */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "20px",
        }}>
          {/* Notification Title */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "14px", textTransform: "capitalize", color: "white" }}>
              Notification Title
            </label>
            <input
              type="text"
              placeholder="Notification Title"
              value={notificationTitle}
              onChange={handleNotificationTitle}
              style={{
                padding: "12px 16px",
                borderRadius: "8px",
                border: "none",
                outline: "none",
                fontSize: "16px",
                width: "100%",
                boxSizing: "border-box",
                backgroundColor: "white",
                color: "#1f2937",
                fontFamily: "inherit",
              }}
            />
          </div>

          {/* Select Users */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "14px", textTransform: "capitalize", color: "white" }}>
              Select Users
            </label>
            <div style={{ color: "#1f2937" }}>
              <MultiSelect
                options={users}
                value={selectFromMS}
                onChange={setselectFromMS}
                labelledBy="Search and select users..."
                valueRenderer={(selected) =>
                  selected.length
                    ? selected.map(({ label }) => label).join(", ")
                    : "No Users Selected"
                }
                ItemRenderer={({ checked, option, onClick, disabled }) => (
                  <div
                    onClick={onClick}
                    style={{ display: "flex", alignItems: "center", gap: "10px", padding: "6px 4px", cursor: "pointer", opacity: disabled ? 0.4 : 1 }}
                  >
                    <input type="checkbox" checked={checked} onChange={() => {}} style={{ flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 500, fontSize: "14px", color: "#1f2937" }}>{option.label}</div>
                      {option.email && (
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>{option.email}</div>
                      )}
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        </div>

        {/* Notification Text */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <label style={{ fontSize: "14px", textTransform: "capitalize", color: "white" }}>
            Notification Text
          </label>
          <textarea
            placeholder="Write something..."
            value={notificationText}
            onChange={handleNotificationTextChange}
            rows={6}
            style={{
              padding: "14px 16px",
              borderRadius: "8px",
              border: "none",
              outline: "none",
              fontSize: "16px",
              width: "100%",
              boxSizing: "border-box",
              resize: "vertical",
              fontFamily: "inherit",
              backgroundColor: "white",
              color: "#1f2937",
            }}
          />
        </div>

        {/* ── Schedule Toggle ─────────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            {/* Toggle pill */}
            <div
              onClick={() => { setScheduleMode(!scheduleMode); setScheduledDateTime(""); }}
              style={{
                width: "44px",
                height: "24px",
                borderRadius: "12px",
                backgroundColor: scheduleMode ? "#3b82f6" : "#6b7280",
                position: "relative",
                cursor: "pointer",
                transition: "background-color 0.2s",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "3px",
                  left: scheduleMode ? "23px" : "3px",
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  backgroundColor: "white",
                  transition: "left 0.2s",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
              />
            </div>
            <span style={{ fontSize: "15px", fontWeight: 500, color: "white" }}>
              {scheduleMode ? "📅 Schedule for later" : "⚡ Send immediately"}
            </span>
          </label>

          {/* Date/time picker — visible only in schedule mode */}
          {scheduleMode && (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ fontSize: "13px", color: "#d1d5db" }}>
                Select Date &amp; Time
              </label>
              <input
                type="datetime-local"
                value={scheduledDateTime}
                min={getMinDateTime()}
                onChange={(e) => setScheduledDateTime(e.target.value)}
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "none",
                  outline: "none",
                  fontSize: "15px",
                  backgroundColor: "white",
                  color: "#1f2937",
                  fontFamily: "inherit",
                  cursor: "pointer",
                  width: "260px",
                  boxSizing: "border-box",
                }}
              />
              {scheduledDateTime && (
                <p style={{ fontSize: "12px", color: "#9ca3af", margin: 0 }}>
                  Will send on: {new Date(scheduledDateTime).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Submit / Schedule Button */}
        <button
          onClick={() => setOpen(true)}
          style={{
            padding: "12px 40px",
            backgroundColor: scheduleMode ? "#3b82f6" : "white",
            color: scheduleMode ? "white" : "#1f2937",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: 600,
            cursor: "pointer",
            textTransform: "capitalize",
            width: "100%",
            maxWidth: "240px",
            fontFamily: "inherit",
            transition: "background-color 0.2s",
          }}
        >
          {scheduleMode ? "📅 Schedule Notification" : "Submit"}
        </button>
      </div>

      {/* ── Sent Notifications Table ─────────────────────────────────────── */}
      <div style={{ marginTop: "40px", overflowX: "auto" }}>
        <NotificationsTable notifications={notificationsList} />
      </div>

      {/* ── Scheduled Notifications Table ────────────────────────────────── */}
      <div style={{ overflowX: "auto" }}>
        <ScheduledNotificationsTable
          scheduledNotifications={scheduledNotificationsList}
          onDelete={handleDeleteScheduled}
        />
      </div>
    </div>
  );
};

export default Notifications;
